import { Injectable } from '@angular/core';
import { delay } from '@helpers/utilities';
import * as signalR from '@microsoft/signalr';
import { HubConnection, HubConnectionState } from '@microsoft/signalr';
import {
  AddPOEAnnotationDTO,
  DeletePOEAnnotationDTO,
  EditPOEAnnotationDTO,
} from '@models/SignalR';
import { AddPoeActivityDto } from '@models/SignalR/Activity/addPoeActivityDto';
import { DeletePoeActivityDto } from '@models/SignalR/Activity/deletePoeActivityDto';
import { EditPoeActivityDto } from '@models/SignalR/Activity/editPoeActivityDto';
import { AddQuestionAnswerDto } from '@models/SignalR/Answer/addQuestionAnswerDto';
import { DeleteQuestionAnswerDto } from '@models/SignalR/Answer/deleteQuestionAnswerDto';
import { EditQuestionAnswerDto } from '@models/SignalR/Answer/editQuestionAnswerDto';
import { MarkParagraphAnswerDto } from '@models/SignalR/Answer/markParagraphAnswerQuestionDto';
import { AddActivityQuestionDto } from '@models/SignalR/Question/addActivityQuestionDto';
import { AnswerQuestionDto } from '@models/SignalR/Question/answerQuestionDto';
import { DeSelectAnswerDto } from '@models/SignalR/Question/deSelectAnswerDto';
import { DeleteActivityQuestionDto } from '@models/SignalR/Question/deleteActivityQuestionDto';
import { EditActivityQuestionDto } from '@models/SignalR/Question/editActivityQuestionDto';
import { SelectAnswerDto } from '@models/SignalR/Question/selectAnswerDto';
import { FillInPOEInputDTO } from '@models/SignalR/fillInPOEInputDTO';
import { AppError } from '@models/appError';
import {
  ApiEndpointsService,
  ApiHttpErrorService,
  ApiHttpService,
  AuthenticationService,
  ToastService,
} from '@services/index';
import { Observable, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { currentTimeString } from '../../_helpers/date-formater';
import { ApiResponse } from '../../_models/ApiResponse/ApiResponse';
import { AnnouncementDto } from '../../_models/Chat/AnnouncementDto';
import { MessageDto } from '../../_models/Chat/MessageDto';
import { ViewMessageDto } from '../../_models/Chat/ViewMessageDto';
import { AnnouncementMessageTypes } from '../../config/enums/announcementMessageTypes';
import { ChatRoomTypes } from '../../config/enums/chatRoomTypes';
import { AddPOEInputDTO } from './../../_models/SignalR/addPOEInputDTO';
import { DeletePOEInputDTO } from './../../_models/SignalR/deletePOEInputDTO';
import { EditPOEInputDTO } from './../../_models/SignalR/editPOEInputDTO';
import { ServerMethods } from './serverMethods';

export interface RealTimeCallBack {
  success?: ((res: any) => void) | null | undefined;
  error?: ((error: AppError) => void) | null | undefined;
}

@Injectable({
  providedIn: 'root',
})
export class RealTimeService {
  public connectingToHub = false;
  private _connectedUsers = [];
  public get connectedUsers() {
    return this._connectedUsers;
  }

  private hubConnection: HubConnection;
  private tenantId: number;
  private connectionClosedManually = false;
  private updatePending = false;
  private startConnectionRetryMsDelay = 7000;
  private startConnectionAttempts = 1;
  private maximumStartConnectionRetryAttempts = 5;
  private keepReStartingConnection = true;
  private connectedGroups: string[] = [];
  private connectedTenantGroups: string[] = [];

  private reconnectSubject = new Subject();
  private sharedStudentAnnouncement = new Subject<AnnouncementDto>();
  private sharedStaffAnnouncement = new Subject<AnnouncementDto>();
  private sharedGroupMessage = new Subject<MessageDto>();
  private sharedConnectedUsers = new Subject<any>();

  private sharedAddedInputs = new Subject<any>();
  private sharedDeletedInputs = new Subject<any>();
  private sharedEditedInputs = new Subject<any>();
  private sharedFilledInInputs = new Subject<any>();

  private sharedAddedAnnotations = new Subject<any>();
  private sharedDeletedAnnotations = new Subject<any>();
  private sharedEditedAnnotations = new Subject<any>();

  private stateChangedSubject = new Subject<HubConnectionState>();
  private isInvokingSubject = new Subject<boolean>();
  private invoking: boolean[] = [];

  public get isInvoking() {
    return this.invoking.length > 0;
  }

  public get isInvokingChanges(): Observable<boolean> {
    return this.isInvokingSubject.asObservable();
  }

  public get connectionStateChanges(): Observable<HubConnectionState> {
    return this.stateChangedSubject.asObservable();
  }

  constructor(
    private httpService: ApiHttpService,
    private endpointsService: ApiEndpointsService,
    private toastService: ToastService,
    private authService: AuthenticationService,
    private httpErrorService: ApiHttpErrorService
  ) {
    this.tenantId = this.authService.currentTenantValue.id;
  }

  public async initConnection() {
    this.connectingToHub = true;
    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(this.endpointsService.hubConnection(), {
        accessTokenFactory: () => this.currentJwt,
        transport: signalR.HttpTransportType.WebSockets,
        skipNegotiation: true,
      })
      .configureLogging(signalR.LogLevel.Error)
      .withAutomaticReconnect([
        // 45 seconds total
        1, 2, 2, 5, 5, 5, 5, 5, 5, 5, 5,
      ])
      .build();

    this.hubConnection.onreconnecting(() => {
      this.stateChangedSubject.next(HubConnectionState.Reconnecting);
    });

    this.hubConnection.onreconnected(() => {
      this.stateChangedSubject.next(HubConnectionState.Connected);
    });

    this.hubConnection.onclose(() => {
      this.stateChangedSubject.next(HubConnectionState.Disconnected);

      if (this.connectionClosedManually) {
        this.connectionClosedManually = false;
        return;
      }
    });

    this.addChatEventListeners();
    this.addPOEEventListeners();
    this.listenForForceLogout();

    await this.start();
  }

  public async stopConnection(reset = false) {
    // if an active invocation is pedning, wait a little and try again
    if (this.isInvoking === true) {
      await delay(100);
      await this.stopConnection(reset);
    } else {
      this.stateChangedSubject.next(HubConnectionState.Disconnecting);
      if (!reset) {
        // this.updatePending = false;
      }
      // If the connection is stopped while the start method is running,
      // we dont want the start method to keep retrying after it fails from us stopping it
      // Using the value of the keepReStartingConnection manage this.
      if (this.hubConnection?.state === signalR.HubConnectionState.Connecting) {
        this.keepReStartingConnection = false;
      }
      this.connectionClosedManually = true;
      await this.hubConnection?.stop();
      this.stateChangedSubject.next(HubConnectionState.Disconnected);
    }
  }

  public retrieveStudentAnnouncements(): Observable<AnnouncementDto> {
    return this.sharedStudentAnnouncement.asObservable();
  }

  public retrieveStaffAnnouncements(): Observable<AnnouncementDto> {
    return this.sharedStaffAnnouncement.asObservable();
  }

  public retrieveGroupMessages(): Observable<MessageDto> {
    return this.sharedGroupMessage.asObservable();
  }

  public retrieveConnectedUsers(): Observable<any> {
    return this.sharedConnectedUsers.asObservable();
  }

  public retrieveAddedInputs(): Observable<any> {
    return this.sharedAddedInputs.asObservable();
  }

  public retrieveDeletedInputs(): Observable<any> {
    return this.sharedDeletedInputs.asObservable();
  }

  public retrieveEditedInputs(): Observable<any> {
    return this.sharedEditedInputs.asObservable();
  }

  public retrieveFilledInInputs(): Observable<any> {
    return this.sharedFilledInInputs.asObservable();
  }

  public retrieveAddedAnnotations(): Observable<any> {
    return this.sharedAddedAnnotations.asObservable();
  }

  public retrieveDeletedAnnotations(): Observable<any> {
    return this.sharedDeletedAnnotations.asObservable();
  }

  public retrieveEditedAnnotations(): Observable<any> {
    return this.sharedEditedAnnotations.asObservable();
  }

  public async joinGroup(groupName: string, rejoin = false) {
    await this.invokeServerMethod(ServerMethods.joinGroup, groupName);
    if (!rejoin) {
      this.connectedGroups.push(groupName);
    }
  }

  public async joinTenantGroup(groupName: string, rejoin = false) {
    await this.invokeServerMethod(ServerMethods.joinTenantGroup, groupName);
    if (!rejoin) {
      this.connectedTenantGroups.push(groupName);
    }
  }

  public async leaveGroup(groupName: string) {
    await this.invokeServerMethod(ServerMethods.leaveGroup, groupName);
    this.connectedGroups = this.connectedGroups.filter(
      (group) => group !== groupName
    );
  }

  public async leaveTenantGroup(groupName: string) {
    await this.invokeServerMethod(ServerMethods.leaveTenantGroup, groupName);
    this.connectedTenantGroups = this.connectedTenantGroups.filter(
      (group) => group !== groupName
    );
  }

  public async addStudentPOEAnnotation(annotation: AddPOEAnnotationDTO) {
    await this.invokeServerMethod(
      ServerMethods.addStudentPOEAnnotation,
      annotation
    );
  }

  public async invokeMethod(
    methodName: string,
    arg1: any,
    arg2: any | null = null
  ) {
    await this.invokeServerMethod(methodName, arg1, arg2);
  }

  public async addGeneralDocInput(input: any) {
    await this.invokeServerMethod(ServerMethods.addGeneralDocInput, input);
  }

  public async deleteGeneralDocInput(input: any) {
    await this.invokeServerMethod(ServerMethods.deleteGeneralDocInput, input);
  }

  public async editGeneralDocInput(input: any) {
    await this.invokeServerMethod(ServerMethods.editGeneralDocInput, input);
  }

  public async fillInGeneralDocInput(input: any) {
    await this.invokeServerMethod(ServerMethods.fillInGeneralDocInput, input);
  }

  public async addProgrammeGeneralDocAnnotation(annotation: any) {
    await this.invokeServerMethod(
      ServerMethods.addProgrammeDocAnnotation,
      annotation
    );
  }

  public async addStudentGeneralDocAnnotation(annotation: any) {
    await this.invokeServerMethod(
      ServerMethods.addStudentDocAnnotation,
      annotation
    );
  }

  public async deleteProgrammeGeneralDocAnnotation(annotation: any) {
    await this.invokeServerMethod(
      ServerMethods.deleteProgrammeDocAnnotation,
      annotation
    );
  }

  public async deleteStudentGeneralDocAnnotation(annotation: any) {
    await this.invokeServerMethod(
      ServerMethods.deleteStudentDocAnnotation,
      annotation
    );
  }

  public async editGeneralDocAnnotation(annotation: any) {
    await this.invokeServerMethod(ServerMethods.editDocAnnotation, annotation);
  }

  public async addProgrammePOEAnnotation(annotation: AddPOEAnnotationDTO) {
    await this.invokeServerMethod(
      ServerMethods.addProgrammePOEAnnotation,
      annotation
    );
  }

  public async deleteStudentPOEAnnotation(annotation: DeletePOEAnnotationDTO) {
    await this.invokeServerMethod(
      ServerMethods.deleteStudentPOEAnnotation,
      annotation
    );
  }

  public async deleteProgrammePOEAnnotation(
    annotation: DeletePOEAnnotationDTO
  ) {
    await this.invokeServerMethod(
      ServerMethods.deleteProgrammePOEAnnotation,
      annotation
    );
  }

  public async editPOEAnnotation(annotation: EditPOEAnnotationDTO) {
    await this.invokeServerMethod(ServerMethods.editPOEAnnotation, annotation);
  }

  public async addPOEInput(input: AddPOEInputDTO) {
    await this.invokeServerMethod(ServerMethods.addPOEInput, input);
  }

  public async deletePOEInput(input: DeletePOEInputDTO) {
    await this.invokeServerMethod(ServerMethods.deletePOEInput, input);
  }

  public async editPOEInput(input: EditPOEInputDTO) {
    await this.invokeServerMethod(ServerMethods.editPOEInput, input);
  }

  public async fillInPOEInput(input: FillInPOEInputDTO) {
    await this.invokeServerMethod(ServerMethods.fillInPOEInput, input);
  }

  public async addPoeActivity(
    activity: AddPoeActivityDto,
    cb: RealTimeCallBack
  ) {
    await this.invokeServerMethod(
      ServerMethods.addPOEActivity,
      activity,
      null,
      cb
    );
  }

  public async editPoeActivity(
    activity: EditPoeActivityDto,
    cb: RealTimeCallBack
  ) {
    await this.invokeServerMethod(
      ServerMethods.editPOEActivity,
      activity,
      null,
      cb
    );
  }

  public async deletePoeActivity(
    activity: DeletePoeActivityDto,
    cb: RealTimeCallBack
  ) {
    await this.invokeServerMethod(
      ServerMethods.deletePOEActivity,
      activity,
      null,
      cb
    );
  }

  public async markParagraphAnswer(
    mark: MarkParagraphAnswerDto,
    cb: RealTimeCallBack
  ) {
    await this.invokeServerMethod(
      ServerMethods.markParagraphAnswer,
      mark,
      null,
      cb
    );
  }

  //QUESTION
  public async answerQuestion(
    question: AnswerQuestionDto,
    cb: RealTimeCallBack
  ) {
    await this.invokeServerMethod(
      ServerMethods.answerQuestion,
      question,
      null,
      cb
    );
  }

  public async deselectAnswer(
    question: DeSelectAnswerDto,
    cb: RealTimeCallBack
  ) {
    await this.invokeServerMethod(
      ServerMethods.deSelectAnswer,
      question,
      null,
      cb
    );
  }

  public async selectAnswer(question: SelectAnswerDto, cb: RealTimeCallBack) {
    await this.invokeServerMethod(
      ServerMethods.selectAnswer,
      question,
      null,
      cb
    );
  }

  public async addActivityQuestion(
    question: AddActivityQuestionDto,
    cb: RealTimeCallBack
  ) {
    await this.invokeServerMethod(
      ServerMethods.addActivityQuestion,
      question,
      null,
      cb
    );
  }

  public async editActivityQuestion(
    question: EditActivityQuestionDto,
    cb: RealTimeCallBack
  ) {
    await this.invokeServerMethod(
      ServerMethods.editActivityQuestion,
      question,
      null,
      cb
    );
  }

  public async deleteActivityQuestion(
    question: DeleteActivityQuestionDto,
    cb: RealTimeCallBack
  ) {
    await this.invokeServerMethod(
      ServerMethods.deleteActivityQuestion,
      question,
      null,
      cb
    );
  }

  //ANSWER
  public async addQuestionAnswer(
    answer: AddQuestionAnswerDto,
    cb: RealTimeCallBack
  ) {
    await this.invokeServerMethod(
      ServerMethods.addQuestionAnswer,
      answer,
      null,
      cb
    );
  }

  public async editQuestionAnswer(
    answer: EditQuestionAnswerDto,
    cb: RealTimeCallBack
  ) {
    await this.invokeServerMethod(
      ServerMethods.editQuestionAnswer,
      answer,
      null,
      cb
    );
  }

  public async deleteQuestionAnswer(
    answer: DeleteQuestionAnswerDto,
    cb: RealTimeCallBack
  ) {
    await this.invokeServerMethod(
      ServerMethods.deleteQuestionAnswer,
      answer,
      null,
      cb
    );
  }

  public sendAnnouncement(
    type: AnnouncementMessageTypes,
    user: string,
    message: string
  ) {
    this.invokeRESTMethod(
      this.endpointsService.sendAnnouncement(),
      {
        type,
        user,
        message,
      },
      'Message sent successfully.'
    );
  }

  public sendSignalRGroupMessage(
    groupName: string,
    message: MessageDto,
    type: ChatRoomTypes
  ) {
    const endpoint =
      type === ChatRoomTypes.learnership
        ? this.endpointsService.sendLearnershipGroupMessage()
        : this.endpointsService.sendInternshipGroupMessage();
    this.invokeRESTMethod(
      endpoint,
      { groupName, message },
      'Message sent successfully.'
    );
  }

  private get currentJwt() {
    return this.authService.jwtToken;
  }

  private async start(isReconnection = false) {
    this.stateChangedSubject.next(HubConnectionState.Connecting);
    this.connectingToHub = true;

    try {
      await this.hubConnection?.start();

      if (isReconnection) {
        this.reconnectSubject.next();
        this.rejoinGroups();
      }

      this.connectingToHub = false;
      this.stateChangedSubject.next(HubConnectionState.Connected);
    } catch (error) {
      this.connectingToHub = false;
      this.stateChangedSubject.next(HubConnectionState.Disconnected);
      if (!this.keepReStartingConnection) {
        return;
      }
      if (
        this.startConnectionAttempts <= this.maximumStartConnectionRetryAttempts
      ) {
        // this.snackBarService.showError(
        //   `${this.snackBarService.formatSignalRError(
        //     error,
        //     true
        //   )}Trying again now... (${this.startConnectionAttempts}/${
        //     this.maximumStartConnectionRetryAttempts
        //   })`,
        //   this.startConnectionRetryMsDelay
        // );
        setTimeout(() => this.start(), this.startConnectionRetryMsDelay);
      } else {
        // this.snackBarService.signalRClose(
        //   `${this.snackBarService.formatSignalRError(error, true)}After ${
        //     this.maximumStartConnectionRetryAttempts
        //   } attempts, a connection could not be made.\n\nPLEASE TRY REFRESH YOUR PAGE.`
        // );
      }
      this.startConnectionAttempts++;
    }
  }

  private async resetConnection() {
    if (this.hubConnection.state === signalR.HubConnectionState.Connected) {
      await this.stopConnection(true);
    }

    // only try start if connection has been disconnected
    if (this.hubConnection.state === signalR.HubConnectionState.Disconnected) {
      await this.start(true);
      return;
    }

    // if we reach this point, connection is either disconnecting or is still connected
    setTimeout(() => this.resetConnection(), 100);
  }

  private async rejoinGroups() {
    // rejoin normal groups
    for (let i = 0; i < this.connectedGroups.length; i++) {
      await this.joinGroup(this.connectedGroups[i], true);
    }

    // rejoin tenant groups
    for (let i = 0; i < this.connectedTenantGroups.length; i++) {
      await this.joinTenantGroup(this.connectedTenantGroups[i], true);
    }
  }

  private listenForStudentAnnouncements() {
    this.hubConnection.on(
      `StudentAnnouncement-${this.tenantId}`,
      this.pipeSignal((announcement: AnnouncementDto) => {
        this.mapRecievedStudentAnnouncement(announcement);
      })
    );
  }

  private listenForStaffAnnouncements() {
    this.hubConnection.on(
      `StaffAnnouncement-${this.tenantId}`,
      this.pipeSignal((announcement: AnnouncementDto) => {
        this.mapRecievedStaffAnnouncement(announcement);
      })
    );
  }

  private listenForChatRoomMessages() {
    this.hubConnection.on(
      `ChatRoomMessage-${this.tenantId}`,
      this.pipeSignal((message: ViewMessageDto) => {
        this.mapRecievedGroupMessage(message);
      })
    );
  }

  // When an update is on the way, clients will be notified every 10 seconds.
  private listenForUpdateAnnouncements() {
    this.hubConnection.on(
      'UpdatePending',
      this.pipeSignal((secondsUntilUpdate: number) => {
        if (!this.updatePending) {
          const updateTime = currentTimeString(true, secondsUntilUpdate);
          const msToUpdate = secondsUntilUpdate * 1000;

          const message =
            secondsUntilUpdate < 60
              ? `The LMS will update in ${secondsUntilUpdate} seconds.
        \n\nPlease save your work and close this page to avoid any loss of data.`
              : `The LMS is ready for an update.
        \n\nThe update will start at ${updateTime}.
        \n\nPlease save your work and close this page to avoid any loss of data.`;

          this.toastService.showWarning(message, msToUpdate);

          this.updatePending = true;

          setTimeout(() => {
            this.authService.logout(true, null, true);
            this.stopConnection();
          }, msToUpdate);
        }
      })
    );
  }

  private listenForConnectedUserUpdates() {
    this.hubConnection.on(
      'UpdateConnectedUsers',
      this.pipeSignal((connectedUsers: any[]) => {
        this.mapRecieveConnectedUserUpdate(connectedUsers);
      })
    );
  }

  private pipeSignal(cb: (arg: any) => void): (arg: any) => void {
    return (arg) => {
      // write code here that gets executed on every event listener
      cb(arg);
    };
  }

  private listenForUserSpecificAnnouncements() {
    this.hubConnection.on(
      'UserSpecificAnnouncement',
      this.pipeSignal((message: string) => {
        this.toastService.showSuccess(message, 15000);
      })
    );
  }

  private mapRecievedStudentAnnouncement(announcement: AnnouncementDto): void {
    this.sharedStudentAnnouncement.next(announcement);
  }

  private mapRecievedStaffAnnouncement(announcement: AnnouncementDto): void {
    this.sharedStaffAnnouncement.next(announcement);
  }

  private mapRecievedGroupMessage(message: MessageDto): void {
    this.sharedGroupMessage.next(message);
  }

  private mapRecieveConnectedUserUpdate(connectedUsers: any): void {
    this._connectedUsers = connectedUsers;
    this.sharedConnectedUsers.next(connectedUsers);
  }

  private listenForForceLogout() {
    this.hubConnection.on(
      'ForceLogout',
      this.pipeSignal(() => {
        this.forcefullyLogoutCurrentUser();
      })
    );
  }

  private forcefullyLogoutCurrentUser() {
    this.authService.forceLogout = true;
    const revokeToken = false;
    this.authService.logout(revokeToken);
  }

  private listenForPOEAddedInputsUpdates() {
    this.hubConnection.on(
      'AddedInput',
      this.pipeSignal((input: any) => {
        this.mapRecieveInputAddedUpdate(input);
      })
    );
  }

  private listenForPOEDeletedInputsUpdates() {
    this.hubConnection.on(
      'DeletedInput',
      this.pipeSignal((input: any) => {
        this.mapRecieveInputDeletedUpdate(input);
      })
    );
  }

  private listenForPOEEditedInputsUpdates() {
    this.hubConnection.on(
      'EditedInput',
      this.pipeSignal((input: any) => {
        this.mapRecieveInputEditedUpdate(input);
      })
    );
  }

  private listenForPOEFilledInInputsUpdates() {
    this.hubConnection.on(
      'FilledInInput',
      this.pipeSignal((input: any) => {
        this.mapRecieveInputFilledInUpdate(input);
      })
    );
  }

  private listenForPOEAddedAnnotationUpdates() {
    this.hubConnection.on(
      'AddedAnnotation',
      this.pipeSignal((annotation: any) => {
        this.mapRecieveAnnotationAddedUpdate(annotation);
      })
    );
  }

  private listenForPOEDeletedAnnotationUpdates() {
    this.hubConnection.on(
      'DeletedAnnotation',
      this.pipeSignal((annotation: any) => {
        this.mapRecieveAnnotationDeletedUpdate(annotation);
      })
    );
  }

  private listenForPOEEditedAnnotationUpdates() {
    this.hubConnection.on(
      'EditedAnnotation',
      this.pipeSignal((annotation: any) => {
        this.mapRecieveAnnotationEditedUpdate(annotation);
      })
    );
  }

  private mapRecieveInputAddedUpdate(input: any): void {
    this.sharedAddedInputs.next(input);
  }

  private mapRecieveInputDeletedUpdate(input: any): void {
    this.sharedDeletedInputs.next(input);
  }

  private mapRecieveInputEditedUpdate(input: any): void {
    this.sharedEditedInputs.next(input);
  }

  private mapRecieveInputFilledInUpdate(input: any): void {
    this.sharedFilledInInputs.next(input);
  }

  private mapRecieveAnnotationAddedUpdate(annotation: any): void {
    this.sharedAddedAnnotations.next(annotation);
  }

  private mapRecieveAnnotationDeletedUpdate(annotation: any): void {
    this.sharedDeletedAnnotations.next(annotation);
  }

  private mapRecieveAnnotationEditedUpdate(annotation: any): void {
    this.sharedEditedAnnotations.next(annotation);
  }

  // // If more than 2 args are needed, rather wrap them in a model
  // // Please refer to IChatHub in the API for methods that can be invoked using this method
  // private async invokeServerMethod(
  //   methodName: string,
  //   arg1: any,
  //   arg2: any = null
  // ) {
  //   try {
  //     // Wait and retry if the hub is busy connecting/re-connecting
  //     if (
  //       this.hubConnection.state === HubConnectionState.Connecting ||
  //       this.hubConnection.state === HubConnectionState.Reconnecting
  //     ) {
  //       setTimeout(async () => {
  //         await this.invokeServerMethod(methodName, arg1, arg2);
  //       }, 100);
  //       return;
  //     }
  //     if (arg2 === null) {
  //       var res = await this.hubConnection.invoke(methodName, arg1);
  //     } else {
  //       var res = await this.hubConnection.invoke(methodName, arg1, arg2);
  //     }

  //     if (res?.succeeded === false) {
  //       this.httpErrorService.handle(new AppError(res.message, res.status));
  //     }
  //   } catch (error) {
  //     // If user is in a chat room or has poe docs open
  //     // and logs out, the signal functions to leave the real time
  //     // groups fail because the hub disconnects the moment we hit log out.
  //     //  This is fine though as signalR removes the connections from the groups automatically.
  //     if (
  //       this.authService.logoutPermitted &&
  //       this.hubConnection.state === HubConnectionState.Disconnected
  //     ) {
  //       return;
  //     }
  //     this.toastService.showError(
  //       this.formatError(`Unable to invoke method '${methodName}'. ${error}`)
  //     );
  //   }
  // }

  private async invokeServerMethod(
    methodName: string,
    arg1: any,
    arg2: any = null,
    cb: RealTimeCallBack = {}
  ) {
    this.invoking.push(true);
    this.isInvokingSubject.next(this.isInvoking);
    // Wait and retry if the hub is busy connecting/re-connecting
    if (
      this.hubConnection?.state === HubConnectionState.Connecting ||
      this.hubConnection?.state === HubConnectionState.Reconnecting
    ) {
      await delay(100);
      await this.invokeServerMethod(methodName, arg1, arg2, cb);
      this.invoking.pop();
      this.isInvokingSubject.next(this.isInvoking);
    } else {
      if (cb.error === null || cb.error === undefined) {
        cb.error = (error: AppError) => {
          this.toastService.showError(error.message, 20000);
        };
      }

      try {
        let res;
        if (arg2 === null) {
          res = await this.hubConnection?.invoke(methodName, arg1);
        } else {
          res = await this.hubConnection?.invoke(methodName, arg1, arg2);
        }

        this.invoking.pop();
        this.isInvokingSubject.next(this.isInvoking);

        if (res?.succeeded === false) {
          throw new AppError(res.message, res.status);
        }

        if (cb.success !== null && cb.success !== undefined) {
          cb.success(res);
        }

        return res;
      } catch (error: any) {
        this.invoking.pop();
        this.isInvokingSubject.next(this.isInvoking);

        // using the penindginInvocation variable I seemed to have solved the below,
        // leaving it here for furture refrernc in case

        // If user is in a group
        // goes to landing page, the signal functions to leave the real time
        // groups fail because the hub disconnects the moment we hit log out.
        //  This is fine though as signalR removes the connections from the groups automatically.
        // const userIsNavigatingToTheLandingPage = this.router.url === '/home';
        // if (
        //   userIsNavigatingToTheLandingPage &&
        //   this.hubConnection?.state === HubConnectionState.Disconnected
        // ) {
        //   return;
        // }

        let errorMessage =
          'An error has occured when trying to save data.\n\nMessage: ';

        const errorStatus = error.status ?? error.Status ?? null;

        errorMessage +=
          errorStatus === 401
            ? 'Your connection is unauthorized.'
            : error.message ?? error.Message ?? 'An unknown error occured.';

        errorMessage +=
          '\n\nPlease refresh your page before making any more changes to prevent unexpected data loss.';

        cb.error(new AppError(errorMessage, errorStatus));
      }
    }
  }

  private invokeRESTMethod(
    endpoint: string,
    payload: any,
    successMessage: string = null
  ) {
    this.httpService
      .post<ApiResponse<any>>(endpoint, payload)
      .pipe(take(1))
      .subscribe(
        () => {
          if (successMessage !== null) {
            this.toastService.showSuccess(successMessage);
          }
        },
        (error: AppError) => {
          this.httpErrorService.handle(error);
        }
      );
  }

  // signalR errors are long ugly strings, using this function to format the
  // error string and add some new lines between multiple sentances
  private formatError(error: any, splitChar = '. '): string {
    const errMsg = error.message as string;
    if (error.statusCode !== null) {
      if (error.statusCode === 401) {
        return 'Your connection is unauthorized.\n\n';
      }
    }
    if (errMsg === 'Failed to fetch') {
      return 'Unable to connect to the server.\n\n';
    }
    const errArr = errMsg.split(splitChar);
    for (let i = 0; i < errArr.length; i++) {
      errArr[i] = `${errArr[i]}.\n\n`;
    }
    return errArr.join('');
  }

  private addChatEventListeners() {
    this.listenForStudentAnnouncements();
    this.listenForStaffAnnouncements();
    this.listenForChatRoomMessages();
    this.listenForConnectedUserUpdates();
    this.listenForUpdateAnnouncements();
    this.listenForUserSpecificAnnouncements();
  }

  private addPOEEventListeners() {
    this.listenForPOEAddedInputsUpdates();
    this.listenForPOEDeletedInputsUpdates();
    this.listenForPOEEditedInputsUpdates();
    this.listenForPOEFilledInInputsUpdates();
    this.listenForPOEAddedAnnotationUpdates();
    this.listenForPOEDeletedAnnotationUpdates();
    this.listenForPOEEditedAnnotationUpdates();
  }
}
